import React from 'react';
import {Box, Chip, CircularProgress, Paper, Typography} from '@mui/material';
import {DataGrid, GridColDef} from '@mui/x-data-grid';
import {useGetComsDailyReportsQuery, ComsDailyReport} from '../../redux/comsApi';

interface Props {
  blockId: string;
}

const COLOR = '#b71c1c';
const fmt = (v: number) =>
  '$' +
  (v ?? 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const LabourSummary: React.FC<Props> = ({blockId}) => {
  const {data: reports = [], isLoading} = useGetComsDailyReportsQuery(blockId);

  const rows: any[] = [];
  for (const r of reports as ComsDailyReport[]) {
    for (const l of (r.labour ?? []) as any[]) {
      rows.push({id: `${r.id}-${l.lineId}`, date: r.reportDate, grade: l.grade, unit: l.unit, people: l.people, dayRate: l.dayRate, amount: l.amount});
    }
  }

  const totalLabour = rows.reduce(
    (sum, row) => sum + (Number(row.amount) || 0),
    0,
  );

  const columns: GridColDef[] = [
    {field: 'date',    headerName: 'Date',       width: 130},
    {field: 'grade',   headerName: 'Grade',      flex: 1},
    {field: 'unit',    headerName: 'Unit',       width: 90},
    {field: 'people',  headerName: 'People',     width: 100, type: 'number'},
    {field: 'dayRate', headerName: 'Day Rate',   width: 120, type: 'number', valueFormatter: (v: any) => `$${(v ?? 0).toFixed(2)}`},
    {field: 'amount',  headerName: 'Amount',     width: 130, type: 'number', valueFormatter: (v: any) => `$${(v ?? 0).toFixed(2)}`},
  ];

  return (
    <Box>
      <Typography variant="h2" sx={{color: COLOR, fontWeight: 600, mb: 3}}>
        Labour — Per-Day Records
      </Typography>
      {!isLoading && (
        <Box display="flex" flexWrap="wrap" gap={1.5} mb={2}>
          <Chip
            label={`Recorded Labour Cost: ${fmt(totalLabour)}`}
            sx={{bgcolor: '#fdecea', color: COLOR, fontWeight: 700}}
          />
          <Chip
            label={`Entries: ${rows.length}`}
            variant="outlined"
            sx={{borderColor: '#f3c7c2', color: COLOR}}
          />
        </Box>
      )}
      <Paper sx={{borderRadius: 2}}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            hideFooterSelectedRowCount
            sx={{border: 0, '& .MuiDataGrid-columnHeaders': {bgcolor: COLOR, color: '#fff'}}}
          />
        )}
      </Paper>
    </Box>
  );
};

export default LabourSummary;
